import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"password" | "magic">("magic");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === "magic") {
        const res = await api.magicLink(email);
        setInfo(
          res.magicLinkDev
            ? `Dev magic link (local only): ${res.magicLinkDev}`
            : res.message
        );
      } else {
        const res = await api.login({ email, password });
        setUser(res.user);
        navigate(next);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="form-page">
      <form className="form-card" onSubmit={(e) => void onSubmit(e)}>
        <h1>Sign in</h1>
        <p className="sub">Magic link preferred. Password optional if you set one.</p>
        {error ? <div className="alert alert-error">{error}</div> : null}
        {info ? <div className="alert alert-info">{info}</div> : null}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
          <button
            type="button"
            className={`btn btn-sm ${mode === "magic" ? "btn-primary" : "btn-outline"}`}
            onClick={() => setMode("magic")}
          >
            Magic link
          </button>
          <button
            type="button"
            className={`btn btn-sm ${mode === "password" ? "btn-primary" : "btn-outline"}`}
            onClick={() => setMode("password")}
          >
            Password
          </button>
        </div>
        <label>
          Email
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </label>
        {mode === "password" ? (
          <label>
            Password
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
        ) : null}
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy} type="submit">
          {busy ? "Please wait…" : mode === "magic" ? "Email me a link" : "Sign in"}
        </button>
        <p className="muted" style={{ marginTop: "1rem", fontSize: "0.9rem" }}>
          No account? <Link to="/register">Create one</Link>
        </p>
      </form>
    </div>
  );
}

export function RegisterPage() {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    companyName: "",
    phone: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.register({
        ...form,
        password: form.password || undefined,
      });
      setUser(res.user);
      if (res.magicLinkDev) setInfo(`Dev verify link: ${res.magicLinkDev}`);
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="form-page">
      <form className="form-card" onSubmit={(e) => void onSubmit(e)}>
        <h1>Create account</h1>
        <p className="sub">Access downloads, guided reports and billing history.</p>
        {error ? <div className="alert alert-error">{error}</div> : null}
        {info ? <div className="alert alert-info">{info}</div> : null}
        <label>
          Full name
          <input
            required
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>
        <label>
          Company
          <input
            value={form.companyName}
            onChange={(e) => setForm({ ...form, companyName: e.target.value })}
          />
        </label>
        <label>
          Phone
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </label>
        <label>
          Password (optional)
          <input
            type="password"
            minLength={8}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Min 8 characters if set"
          />
        </label>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy} type="submit">
          {busy ? "Creating…" : "Create account"}
        </button>
        <p className="muted" style={{ marginTop: "1rem", fontSize: "0.9rem" }}>
          Already registered? <Link to="/login">Sign in</Link>
        </p>
      </form>
    </div>
  );
}

export function MagicPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      if (!token) {
        setError("Missing token");
        return;
      }
      try {
        const res = await api.magicConsume(token);
        setUser(res.user);
        navigate("/dashboard");
      } catch (e) {
        setError(e instanceof Error ? e.message : "Invalid link");
      }
    })();
  }, [token, setUser, navigate]);

  return (
    <div className="form-page">
      <div className="form-card">
        <h1>Signing you in…</h1>
        {error ? (
          <>
            <div className="alert alert-error">{error}</div>
            <Link className="btn btn-primary" to="/login">
              Back to sign in
            </Link>
          </>
        ) : (
          <p className="muted">Validating magic link.</p>
        )}
      </div>
    </div>
  );
}
